type Rgba = { r: number; g: number; b: number; a: number }

const parseHex = (hex: string): Rgba => {
  const s = hex.replace('#', '')
  const full =
    s.length === 3
      ? s
          .split('')
          .map((c) => c + c)
          .join('')
      : s
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
    a: full.length === 8 ? Number.parseInt(full.slice(6, 8), 16) / 255 : 1,
  }
}

/** Hoà màu có alpha lên nền đục. Bắt buộc vì neutral của ta dùng alpha. */
const composite = (fg: Rgba, bg: Rgba): Rgba => ({
  r: fg.r * fg.a + bg.r * (1 - fg.a),
  g: fg.g * fg.a + bg.g * (1 - fg.a),
  b: fg.b * fg.a + bg.b * (1 - fg.a),
  a: 1,
})

const channel = (v: number) => {
  const c = v / 255
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

const luminance = ({ r, g, b }: Rgba) =>
  0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)

/** Tỷ lệ tương phản theo WCAG 2.x. AAA cho chữ thường là 7:1, chữ phụ 4.5:1. */
export const contrastRatio = (foreground: string, background: string): number => {
  const bg = parseHex(background)
  const fg = composite(parseHex(foreground), bg)
  const l1 = luminance(fg)
  const l2 = luminance(bg)
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1]
  return (hi + 0.05) / (lo + 0.05)
}

/** Chữ trắng hay đen đọc rõ hơn trên nền `background` (chọn theo tỷ lệ tương phản cao hơn). */
export const readableOn = (background: string): '#FFFFFF' | '#000000' =>
  contrastRatio('#FFFFFF', background) >= contrastRatio('#000000', background)
    ? '#FFFFFF'
    : '#000000'

/** Chuỗi hex đầy đủ 6 ký tự (`#RRGGBB`) — dùng để lọc dữ liệu màu người dùng/localStorage. */
export const isHexColor = (v: unknown): v is string =>
  typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)

/** Tương phản của chữ thường (neutral primary của theme `mode`) trên nền `background`. */
export const bodyTextContrast = (mode: 'light' | 'dark', background: string): number =>
  contrastRatio(mode === 'dark' ? '#FFFFFFD9' : '#000000E0', background)
